// Currency and payment methods by country
export type CurrencyConfig = {
  code: string;
  symbol: string;
  name: string;
  freePrice: number;
  proPrice: number;
  proYearlyPrice: number;
  locale: string;
};

export type PaymentMethod = {
  id: string;
  name: string;
  icon: string;
  description: string;
};

export type CountryPaymentConfig = {
  currency: CurrencyConfig;
  paymentMethods: PaymentMethod[];
};

const PAYMENT_METHODS: Record<string, PaymentMethod> = {
  card: { id: "card", name: "Carte bancaire", icon: "💳", description: "Visa, Mastercard" },
  mobileMoney: { id: "mobile_money", name: "Mobile Money", icon: "📱", description: "MTN, Orange Money" },
  airtelMoney: { id: "airtel_money", name: "Airtel Money", icon: "📲", description: "Airtel Money" },
  moovMoney: { id: "moov_money", name: "Moov Money", icon: "📱", description: "Moov Africa" },
  orangeMoney: { id: "orange_money", name: "Orange Money", icon: "🟠", description: "Orange Money" },
  mtnMoney: { id: "mtn_money", name: "MTN Mobile Money", icon: "🟡", description: "MTN MoMo" },
  mpesa: { id: "mpesa", name: "M-Pesa", icon: "📱", description: "Safaricom M-Pesa" },
  wave: { id: "wave", name: "Wave", icon: "🌊", description: "Wave Mobile Money" },
  paypal: { id: "paypal", name: "PayPal", icon: "🅿️", description: "PayPal" },
};

// EUR = base price 9.99€
const COUNTRY_CONFIGS: Record<string, CountryPaymentConfig> = {
  FR: {
    currency: { code: "EUR", symbol: "€", name: "Euro", freePrice: 0, proPrice: 9.99, proYearlyPrice: 99.99, locale: "fr-FR" },
    paymentMethods: [PAYMENT_METHODS.card, PAYMENT_METHODS.paypal],
  },
  BE: {
    currency: { code: "EUR", symbol: "€", name: "Euro", freePrice: 0, proPrice: 9.99, proYearlyPrice: 99.99, locale: "fr-BE" },
    paymentMethods: [PAYMENT_METHODS.card, PAYMENT_METHODS.paypal],
  },
  CH: {
    currency: { code: "CHF", symbol: "CHF", name: "Franc suisse", freePrice: 0, proPrice: 9.90, proYearlyPrice: 99, locale: "fr-CH" },
    paymentMethods: [PAYMENT_METHODS.card, PAYMENT_METHODS.paypal],
  },
  CA: {
    currency: { code: "CAD", symbol: "CA$", name: "Dollar canadien", freePrice: 0, proPrice: 14.99, proYearlyPrice: 149.99, locale: "fr-CA" },
    paymentMethods: [PAYMENT_METHODS.card, PAYMENT_METHODS.paypal],
  },
  // Afrique de l'Ouest - FCFA (XOF)
  SN: {
    currency: { code: "XOF", symbol: "FCFA", name: "Franc CFA (BCEAO)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-SN" },
    paymentMethods: [PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.wave, PAYMENT_METHODS.mtnMoney, PAYMENT_METHODS.card],
  },
  CI: {
    currency: { code: "XOF", symbol: "FCFA", name: "Franc CFA (BCEAO)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-CI" },
    paymentMethods: [PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.mtnMoney, PAYMENT_METHODS.moovMoney, PAYMENT_METHODS.wave, PAYMENT_METHODS.card],
  },
  ML: {
    currency: { code: "XOF", symbol: "FCFA", name: "Franc CFA (BCEAO)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-ML" },
    paymentMethods: [PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.moovMoney, PAYMENT_METHODS.card],
  },
  BF: {
    currency: { code: "XOF", symbol: "FCFA", name: "Franc CFA (BCEAO)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-BF" },
    paymentMethods: [PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.moovMoney, PAYMENT_METHODS.card],
  },
  TG: {
    currency: { code: "XOF", symbol: "FCFA", name: "Franc CFA (BCEAO)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-TG" },
    paymentMethods: [PAYMENT_METHODS.moovMoney, PAYMENT_METHODS.card],
  },
  BJ: {
    currency: { code: "XOF", symbol: "FCFA", name: "Franc CFA (BCEAO)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-BJ" },
    paymentMethods: [PAYMENT_METHODS.mtnMoney, PAYMENT_METHODS.moovMoney, PAYMENT_METHODS.card],
  },
  NE: {
    currency: { code: "XOF", symbol: "FCFA", name: "Franc CFA (BCEAO)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-NE" },
    paymentMethods: [PAYMENT_METHODS.airtelMoney, PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.card],
  },
  GN: {
    currency: { code: "GNF", symbol: "GNF", name: "Franc guinéen", freePrice: 0, proPrice: 50000, proYearlyPrice: 500000, locale: "fr-GN" },
    paymentMethods: [PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.mtnMoney, PAYMENT_METHODS.card],
  },
  // Afrique Centrale - FCFA (XAF)
  CM: {
    currency: { code: "XAF", symbol: "FCFA", name: "Franc CFA (BEAC)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-CM" },
    paymentMethods: [PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.mtnMoney, PAYMENT_METHODS.card],
  },
  GA: {
    currency: { code: "XAF", symbol: "FCFA", name: "Franc CFA (BEAC)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-GA" },
    paymentMethods: [PAYMENT_METHODS.airtelMoney, PAYMENT_METHODS.mobileMoney, PAYMENT_METHODS.card],
  },
  CG: {
    currency: { code: "XAF", symbol: "FCFA", name: "Franc CFA (BEAC)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-CG" },
    paymentMethods: [PAYMENT_METHODS.airtelMoney, PAYMENT_METHODS.mtnMoney, PAYMENT_METHODS.card],
  },
  TD: {
    currency: { code: "XAF", symbol: "FCFA", name: "Franc CFA (BEAC)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-TD" },
    paymentMethods: [PAYMENT_METHODS.airtelMoney, PAYMENT_METHODS.moovMoney, PAYMENT_METHODS.card],
  },
  CF: {
    currency: { code: "XAF", symbol: "FCFA", name: "Franc CFA (BEAC)", freePrice: 0, proPrice: 3000, proYearlyPrice: 30000, locale: "fr-CF" },
    paymentMethods: [PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.card],
  },
  // Congo RDC
  CD: {
    currency: { code: "CDF", symbol: "FC", name: "Franc congolais", freePrice: 0, proPrice: 15000, proYearlyPrice: 150000, locale: "fr-CD" },
    paymentMethods: [PAYMENT_METHODS.airtelMoney, PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.mobileMoney, PAYMENT_METHODS.card],
  },
  // Afrique de l'Est
  KE: {
    currency: { code: "KES", symbol: "KSh", name: "Shilling kényan", freePrice: 0, proPrice: 1500, proYearlyPrice: 15000, locale: "en-KE" },
    paymentMethods: [PAYMENT_METHODS.mpesa, PAYMENT_METHODS.airtelMoney, PAYMENT_METHODS.card],
  },
  RW: {
    currency: { code: "RWF", symbol: "FRw", name: "Franc rwandais", freePrice: 0, proPrice: 8000, proYearlyPrice: 80000, locale: "fr-RW" },
    paymentMethods: [PAYMENT_METHODS.mtnMoney, PAYMENT_METHODS.airtelMoney, PAYMENT_METHODS.card],
  },
  BI: {
    currency: { code: "BIF", symbol: "FBu", name: "Franc burundais", freePrice: 0, proPrice: 20000, proYearlyPrice: 200000, locale: "fr-BI" },
    paymentMethods: [PAYMENT_METHODS.mobileMoney, PAYMENT_METHODS.card],
  },
  // Madagascar
  MG: {
    currency: { code: "MGA", symbol: "Ar", name: "Ariary malgache", freePrice: 0, proPrice: 25000, proYearlyPrice: 250000, locale: "fr-MG" },
    paymentMethods: [PAYMENT_METHODS.orangeMoney, PAYMENT_METHODS.airtelMoney, PAYMENT_METHODS.mobileMoney, PAYMENT_METHODS.card],
  },
  // Maroc
  MA: {
    currency: { code: "MAD", symbol: "DH", name: "Dirham marocain", freePrice: 0, proPrice: 99, proYearlyPrice: 999, locale: "fr-MA" },
    paymentMethods: [PAYMENT_METHODS.card, PAYMENT_METHODS.paypal],
  },
  // Tunisie
  TN: {
    currency: { code: "TND", symbol: "DT", name: "Dinar tunisien", freePrice: 0, proPrice: 29, proYearlyPrice: 290, locale: "fr-TN" },
    paymentMethods: [PAYMENT_METHODS.card],
  },
  // Algérie
  DZ: {
    currency: { code: "DZD", symbol: "DA", name: "Dinar algérien", freePrice: 0, proPrice: 1500, proYearlyPrice: 15000, locale: "fr-DZ" },
    paymentMethods: [PAYMENT_METHODS.card],
  },
  // Haïti
  HT: {
    currency: { code: "HTG", symbol: "G", name: "Gourde haïtienne", freePrice: 0, proPrice: 1000, proYearlyPrice: 10000, locale: "fr-HT" },
    paymentMethods: [PAYMENT_METHODS.mobileMoney, PAYMENT_METHODS.card],
  },
  // UK
  GB: {
    currency: { code: "GBP", symbol: "£", name: "Livre sterling", freePrice: 0, proPrice: 8.99, proYearlyPrice: 89.99, locale: "en-GB" },
    paymentMethods: [PAYMENT_METHODS.card, PAYMENT_METHODS.paypal],
  },
  // USA
  US: {
    currency: { code: "USD", symbol: "$", name: "Dollar US", freePrice: 0, proPrice: 9.99, proYearlyPrice: 99.99, locale: "en-US" },
    paymentMethods: [PAYMENT_METHODS.card, PAYMENT_METHODS.paypal],
  },
};

// Default fallback
const DEFAULT_CONFIG: CountryPaymentConfig = {
  currency: { code: "EUR", symbol: "€", name: "Euro", freePrice: 0, proPrice: 9.99, proYearlyPrice: 99.99, locale: "fr-FR" },
  paymentMethods: [PAYMENT_METHODS.card],
};

export function getCountryConfig(countryCode: string): CountryPaymentConfig {
  return COUNTRY_CONFIGS[countryCode.toUpperCase()] || DEFAULT_CONFIG;
}

export function formatPrice(amount: number, currency: CurrencyConfig): string {
  if (amount === 0) return `0${currency.symbol}`;
  // For FCFA and similar, no decimals
  const noDecimals = ["XOF", "XAF", "CDF", "GNF", "RWF", "BIF", "MGA", "KES", "HTG", "DZD"];
  const decimals = noDecimals.includes(currency.code) ? 0 : 2;

  const formatted = amount.toLocaleString("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  // Place symbol after for most African currencies
  const symbolAfter = ["XOF", "XAF", "CDF", "GNF", "RWF", "BIF", "MGA", "MAD", "TND", "DZD", "HTG"];
  if (symbolAfter.includes(currency.code)) {
    return `${formatted} ${currency.symbol}`;
  }
  return `${currency.symbol}${formatted}`;
}

// Detect country from browser timezone/locale
export function detectCountryCode(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const tzMap: Record<string, string> = {
      "Africa/Brazzaville": "CG", "Africa/Kinshasa": "CD", "Africa/Lubumbashi": "CD",
      "Africa/Douala": "CM", "Africa/Libreville": "GA", "Africa/Ndjamena": "TD", "Africa/Bangui": "CF",
      "Africa/Dakar": "SN", "Africa/Abidjan": "CI", "Africa/Bamako": "ML",
      "Africa/Ouagadougou": "BF", "Africa/Lome": "TG", "Africa/Porto-Novo": "BJ", "Africa/Cotonou": "BJ",
      "Africa/Niamey": "NE", "Africa/Conakry": "GN",
      "Africa/Nairobi": "KE", "Africa/Kigali": "RW", "Africa/Bujumbura": "BI",
      "Indian/Antananarivo": "MG",
      "Africa/Casablanca": "MA", "Africa/Tunis": "TN", "Africa/Algiers": "DZ",
      "America/Port-au-Prince": "HT",
      "Europe/Paris": "FR", "Europe/Brussels": "BE", "Europe/Zurich": "CH",
      "America/Toronto": "CA", "America/Montreal": "CA",
      "Europe/London": "GB",
      "America/New_York": "US", "America/Chicago": "US", "America/Los_Angeles": "US",
    };
    if (tzMap[tz]) return tzMap[tz];

    // Fallback: check navigator language
    const lang = navigator.language || "";
    if (lang.startsWith("fr-")) {
      const region = lang.split("-")[1]?.toUpperCase();
      if (region && COUNTRY_CONFIGS[region]) return region;
    }
  } catch {}
  return "FR"; // default
}
